'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useLocale } from 'next-intl'
import { X } from 'lucide-react'
import { LEAD_FORMS, renderForm } from '@/lib/infiteq-form'

const MIN_TIME_MS = 45_000
const MIN_SCROLL = 0.5
// Exit intent alone fires on any mouse move to the tab bar, so it needs some engagement first
const EXIT_MIN_TIME_MS = 20_000
const EXIT_MIN_SCROLL = 0.25
const DISMISS_DAYS = 7
const STORAGE_KEY = 'lead_popup'
const EXCLUDED = ['/contact', '/leads-offer', '/leads-offer-short', '/offer-booked', '/privacy-policy', '/terms']

function readState(): { submitted?: boolean; dismissedAt?: number } {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')
  } catch {
    return {}
  }
}

function writeState(state: { submitted?: boolean; dismissedAt?: number }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {}
}

function isSuppressed() {
  const { submitted, dismissedAt } = readState()
  if (submitted) return true
  return !!dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86_400_000
}

/**
 * Engaged-visitor lead form: opens after 45 s on site plus 50% scroll, or on desktop exit intent.
 * Time is counted per visit, not per page, so reading across pages counts.
 */
export function LeadFormPopup() {
  const locale = useLocale()
  const pathname = usePathname()
  const formId = LEAD_FORMS.website[locale]
  const excluded = EXCLUDED.some((p) => pathname.replace(/\/$/, '').endsWith(p))

  const [open, setOpen] = useState(false)
  const mountRef = useRef<HTMLDivElement>(null)
  const startRef = useRef(Date.now())

  useEffect(() => {
    if (!formId || excluded || open || isSuppressed()) return

    let scrolled = false
    let exitReady = false
    const tryOpen = () => {
      if (scrolled && Date.now() - startRef.current >= MIN_TIME_MS) setOpen(true)
    }
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      const ratio = max > 0 ? window.scrollY / max : 0
      if (ratio >= MIN_SCROLL) scrolled = true
      if (ratio >= EXIT_MIN_SCROLL) exitReady = true
      tryOpen()
    }
    const onExit = (e: MouseEvent) => {
      if (!exitReady || Date.now() - startRef.current < EXIT_MIN_TIME_MS) return
      if (e.clientY <= 0 && !e.relatedTarget && window.matchMedia('(pointer: fine)').matches) setOpen(true)
    }

    const timer = window.setInterval(tryOpen, 1_000)
    window.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('mouseout', onExit)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('scroll', onScroll)
      document.removeEventListener('mouseout', onExit)
    }
  }, [formId, excluded, open, pathname])

  useEffect(() => {
    const el = mountRef.current
    if (!open || !el || !formId) return
    void renderForm(el, formId, {
      onSuccess: () => writeState({ submitted: true }),
      onDisqualified: () => writeState({ submitted: true }),
    })
  }, [open, formId])

  // Navigating onto an excluded page closes a popup that opened elsewhere
  useEffect(() => {
    if (excluded) setOpen(false)
  }, [excluded])

  if (!open || !formId) return null

  const close = () => {
    if (!readState().submitted) writeState({ dismissedAt: Date.now() })
    setOpen(false)
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      className="fixed bottom-4 left-4 right-4 z-50 max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border border-border bg-white p-4 shadow-2xl sm:right-auto sm:w-[420px]"
    >
      <button
        type="button"
        onClick={close}
        aria-label={locale === 'sr' ? 'Zatvori' : 'Close'}
        className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-border bg-white text-text-muted hover:border-blue hover:text-blue"
      >
        <X size={16} strokeWidth={2} />
      </button>
      <div ref={mountRef} data-lang={locale} />
    </div>
  )
}
